// Hook for managing synchronized table statuses across components
import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { calculateDateAwareTableStatuses } from '../utils/tableStatusUtils';
import { tableApi } from '../services/tableApi';
import webSocketService from '../services/webSocketService';
import type { RestaurantTable } from '../types/reservations';

/**
 * Hook to manage synchronized table statuses across components
 * Automatically refreshes table statuses based on reservations and WebSocket updates
 * Optimized with caching and debouncing to reduce API calls
 */
export const useTableStatusSync = (refreshInterval: number = 0) => { // Default to 0 (no auto-refresh)
  const [tables, setTables] = useState<RestaurantTable[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshTime = useRef<number>(0);
  
  // Minimum time between refreshes (in milliseconds) to prevent excessive API calls
  const MIN_REFRESH_INTERVAL = 5000; // 5 seconds

  // Function to load and calculate table statuses with debouncing
  const refreshTableStatuses = useCallback(async (force: boolean = false) => {
    const now = Date.now();
    
    // Prevent excessive refreshes unless forced
    if (!force && now - lastRefreshTime.current < MIN_REFRESH_INTERVAL) {
      // if (process.env.NODE_ENV === 'development') {
      //   console.log('⚡ Skipping table refresh - too frequent');
      // }
      return;
    }
    
    try {
      setIsLoading(true);
      lastRefreshTime.current = now;
      
      // Get all tables from backend
      const allTables = await tableApi.getAllTablesFlat();
      
      // Apply date-aware status calculation
      const tablesWithStatus = await calculateDateAwareTableStatuses(allTables);
      
      setTables(tablesWithStatus);
      setLastUpdated(new Date());
    } catch (error) {
      toast.error('Failed to refresh table statuses');
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array to prevent recreation

  // Debounced version for WebSocket updates
  const debouncedRefresh = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      refreshTableStatuses();
    }, 2000); // 2 second debounce
  }, [refreshTableStatuses]);

  // Set up periodic refresh
  useEffect(() => {
    // Initial load
    refreshTableStatuses();
    
    // Set up interval for periodic refresh
    if (refreshInterval > 0) {
      intervalRef.current = setInterval(refreshTableStatuses, refreshInterval);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [refreshTableStatuses, refreshInterval]);

  // Listen for WebSocket updates via the existing callback pattern with debouncing
  useEffect(() => {
    const componentId = 'TableStatusSync';
    
    // Register component and set up callback for booking updates
    webSocketService.registerComponent(componentId);
    
    // Subscribe to booking updates to refresh table statuses (debounced)
    const updatesSub = webSocketService.pendingBookings().subscribe({
      next: () => {
        debouncedRefresh(); // Use debounced refresh to prevent excessive calls
      },
      error: (error) => {
        //console.error('WebSocket booking update error:');
      }
    });
    
    return () => {
      webSocketService.unregisterComponent(componentId);
      updatesSub.unsubscribe();
      // Clear any pending debounced calls
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [debouncedRefresh]);

  return {
    tables,
    isLoading,
    lastUpdated,
    refreshTableStatuses
  };
};